import { Dropdown, Input, OverlayContainer, PrimaryButton, Radio, ReactIcons, SimpleButton } from "@kalyzee/kalyzee-ui";
import { MeetingCreateDTO, MeetingOutputDTO, MeetingPrivacy, MeetingSessionRole } from "@kalyzee/kast-web-sdk/http";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useSdkContext } from "../../contexts/sdk/context";
import styles from "./CreateMeetingPopup.module.css";

const PRIVACY_LABELS: Record<MeetingPrivacy, string> = {
  [MeetingPrivacy.PUBLIC]: "Public — tout le monde peut rejoindre",
  [MeetingPrivacy.PRIVATE]: "Privé — seuls les invités peuvent rejoindre",
  [MeetingPrivacy.CLOSED]: "Fermé — personne ne peut rejoindre",
};

const PRIVACY_OPTIONS: MeetingPrivacy[] = [
  MeetingPrivacy.PUBLIC,
  MeetingPrivacy.PRIVATE,
  MeetingPrivacy.CLOSED,
];

interface FieldErrors {
  name?: string;
  password?: string;
  global?: string;
}

export interface CreateMeetingPopupProps {
  visible: boolean;
  onClose: () => void;
  onCreated: (meeting: MeetingOutputDTO) => void;
}

export const CreateMeetingPopup: React.FC<CreateMeetingPopupProps> = ({ visible, onClose, onCreated }) => {
  const sdkCtx = useSdkContext();

  const [name, setName] = useState("");
  const [privacy, setPrivacy] = useState<MeetingPrivacy>(MeetingPrivacy.PUBLIC);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [newcomersAsSpectators, setNewcomersAsSpectators] = useState(true);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Reset state when the popup is closed so it opens fresh next time
  useEffect(() => {
    if (!visible) {
      setName("");
      setPrivacy(MeetingPrivacy.PUBLIC);
      setUsePassword(false);
      setPassword("");
      setNewcomersAsSpectators(true);
      setErrors({});
    }
  }, [visible]);

  const validate = useCallback((): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Le nom est requis.";
    if (usePassword && !password.trim()) next.password = "Le mot de passe est requis.";
    return next;
  }, [name, usePassword, password]);

  const canSubmit = !validate().name && !validate().password;

  const onSubmit = useCallback(async () => {
    const validationErrors = validate();
    if (validationErrors.name || validationErrors.password) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const payload: MeetingCreateDTO = {
        name: name.trim(),
        privacy,
        usePassword,
        defaultRoleOfNewSessionByUserRole: {
          default: newcomersAsSpectators ? MeetingSessionRole.SPECTATOR : MeetingSessionRole.NORMAL,
        },
        ...(usePassword ? { password: password.trim() } : {}),
      };
      const res = await sdkCtx.client.getHttpClient().meeting.create(payload);
      const created = res.data;
      if (!created?._id) {
        setErrors({ global: "Le meeting a été créé mais son identifiant est introuvable." });
        return;
      }
      onCreated(created);
    } catch (err) {
      setErrors({ global: "Impossible de créer le meeting. Réessayez." });
    }
  }, [validate, name, privacy, usePassword, password, newcomersAsSpectators, sdkCtx.client, onCreated]);

  return (
    <OverlayContainer
      visible={visible}
      style={{ position: "fixed", inset: 0, zIndex: 1000 }}
      onClose={onClose}
      overlay={
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <PrimaryButton className={styles.closeBtn} onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>

          <div className={styles.header}>
            <h2 className={styles.title}>Créer un meeting instantané</h2>
            <p className={styles.subtitle}>Configurez rapidement votre meeting avant de le rejoindre.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Nom du meeting</label>
            <Input
              className={styles.input}
              nativeProps={{
                value: name,
                onChange: (e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                },
                placeholder: "Mathématiques — cours du mardi",
                maxLength: 120,
              }}
            />
            {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
          </div>

          {/* <div className={styles.field}>
            <label className={styles.label}>Confidentialité</label>
            <Dropdown
              className={styles.dropdown}
              width="100%"
              options={PRIVACY_OPTIONS}
              selected={privacy}
              onSelect={setPrivacy}
              getLabel={(option) => PRIVACY_LABELS[option]}
            />
          </div> */}

          {/* <div className={styles.inlineField}>
            <div className={styles.inlineFieldText}>
              <span className={styles.label}>Protéger par mot de passe</span>
              <span className={styles.hint}>Un mot de passe sera demandé pour rejoindre.</span>
            </div>
            <Switch value={usePassword} onChange={(v) => {
              setUsePassword(v);
              if (!v && errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
            }} />
          </div> */}

          {/* {usePassword && (
            <div className={styles.field}>
              <label className={styles.label}>Mot de passe</label>
              <Input
                className={styles.input}
                nativeProps={{
                  value: password,
                  onChange: (e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                  },
                  type: "text",
                  placeholder: "Choisissez un mot de passe",
                  maxLength: 60,
                  autoComplete: "off",
                }}
              />
              {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
            </div>
          )} */}

          <div className={styles.modeCards}>
            <button
              type="button"
              className={clsx(styles.modeCard, newcomersAsSpectators && styles.modeCardSelected)}
              onClick={() => setNewcomersAsSpectators(true)}
            >
              <Radio
                value={newcomersAsSpectators}
                style={!newcomersAsSpectators ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
              />
              <div className={styles.modeCardText}>
                <span className={styles.label}>Mode séminaire</span>
                <span className={styles.hint}>
                  Seuls les intervenants et les participants au tableau peuvent parler.
                </span>
              </div>
            </button>
            <button
              type="button"
              className={clsx(styles.modeCard, !newcomersAsSpectators && styles.modeCardSelected)}
              onClick={() => setNewcomersAsSpectators(false)}
            >
              <Radio
                value={!newcomersAsSpectators}
                style={newcomersAsSpectators ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
              />
              <div className={styles.modeCardText}>
                <span className={styles.label}>Mode libre</span>
                <span className={styles.hint}>
                  Tout le monde peut parler.
                </span>
              </div>
            </button>
          </div>

          {errors.global && <span className={styles.fieldError}>{errors.global}</span>}

          <SimpleButton
            className={clsx(styles.submitBtn, !canSubmit && styles.submitBtnDisabled)}
            label="Créer et rejoindre"
            onClick={onSubmit}
          />
        </div>
      }
    />
  );
};
